import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { getSource } from '@/sources/registry';
import { JMCOMIC_SOURCE_ID, parseJmAlbumLookup } from '@/sources/jmcomic/source';
import type {
  ComicCard as ComicCardData,
  Page,
  SearchQuery as PageSearchQuery,
  SearchFiltersSchema,
} from '@/sources/types';
import { ComicCard } from '@/components/ComicCard';
import { PaginatedGrid } from '@/components/PaginatedGrid';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useSourceAuth } from '@/features/auth';
import { BlockedComicCard, useBlacklist } from '@/features/blacklist';
import {
  FilterPanel,
  SaveSearchDialog,
  SavedSearchesMenu,
  SearchBar,
  countActiveFilters,
  isEmptyQuery,
  searchQueryToUrlParams,
  useSearchUrlState,
} from '@/features/search';
import { createSavedSearch } from '@/db/savedSearches';
import { requestAccountDataAutoSync } from '@/features/account/accountDataAutoSync';
import { SourceErrorBanner } from '@/features/reader';
import {
  useRestoreComicCardOnReturn,
  useScrollRestoration,
} from '@/lib/useScrollRestoration';

/**
 * Source landing + search page. Composition:
 *
 *  1. Header with the source name + Connect Account link.
 *  2. SearchBar driven from URL state (`?q=&sort=&lang=&inc=&exc=&f.X=`).
 *  3. Optional filter panel (visible when `searchFilters` is declared and
 *     the user toggles it open).
 *  4. PaginatedGrid driven by `usePaginatedFetcher` — calls
 *     `source.search(query)` when the query is non-empty, otherwise
 *     `source.getPopular()`.
 *  5. Saved searches dropdown / save dialog.
 *
 * URL state is the source of truth: navigating with the back button or
 * sharing a URL replays the search exactly.
 */
export function SourcePage() {
  const { sourceId } = useParams<{ sourceId: string }>();
  const source = sourceId ? getSource(sourceId) : undefined;
  const { isBlocked } = useBlacklist();
  const location = useLocation();
  const navigate = useNavigate();
  const { auth, ready: authReady } = useSourceAuth(source);

  const [asyncSchema, setAsyncSchema] = useState<SearchFiltersSchema | undefined>(undefined);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const schema = asyncSchema ?? source?.searchFilters;
  const { query, setQuery } = useSearchUrlState(schema);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState(query.q ?? '');
  useScrollRestoration('source');
  useRestoreComicCardOnReturn();

  useEffect(() => {
    setSearchDraft(query.q ?? '');
  }, [query.q]);

  useEffect(() => {
    let cancelled = false;
    setAsyncSchema(undefined);
    setSchemaError(null);
    if (!source?.getSearchFilters) {
      setSchemaLoading(false);
      return;
    }
    setSchemaLoading(true);
    source
      .getSearchFilters(auth)
      .then((next) => {
        if (!cancelled) setAsyncSchema(next);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setSchemaError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (!cancelled) setSchemaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [source, auth]);

  // Stable fetcher reference per (source + query). Pagination hook refires
  // when the dependency list changes, so we serialize the query for use as
  // a primitive cache key.
  const queryKey = useMemo(() => searchQueryToUrlParams(query).toString(), [query]);

  const fetchPage = useCallback(
    async (page: number, cursor?: string): Promise<Page<ComicCardData>> => {
      if (!source) {
        throw new Error('No source registered for this page.');
      }
      if (isEmptyQuery(query)) {
        if (source.capabilities.search) {
          return source.search({ page, cursor }, auth);
        }
        return source.getPopular({ page, cursor }, auth);
      }
      return source.search({ ...query, page, cursor }, auth);
    },
    [source, query, auth],
  );

  const activeFiltersCount = countActiveFilters(query);
  const hasAnyQuery = !isEmptyQuery(query);
  const isJmSource = source?.id === JMCOMIC_SOURCE_ID;

  function clearFilters() {
    setQuery(withDraftKeyword({}));
  }

  function withDraftKeyword(next: PageSearchQuery): PageSearchQuery {
    const keyword = searchDraft.trim();
    if (keyword) return { ...next, q: keyword };
    const updated = { ...next };
    delete updated.q;
    return updated;
  }

  function handleSearchSubmit(next: string) {
    setSearchDraft(next);
    const albumId = isJmSource ? parseJmAlbumLookup(next) : undefined;
    if (source && albumId) {
      navigate(`/source/${source.id}/comic/${encodeURIComponent(albumId)}`, {
        state: {
          back: {
            fromPath: `${location.pathname}${location.search}`,
            fromLabel: source.name,
          },
        },
      });
      return;
    }

    const updated = { ...query };
    if (next) updated.q = next;
    else delete updated.q;
    setQuery(updated);
  }

  const renderItem = (c: ComicCardData) =>
    isBlocked(c) ? (
      <BlockedComicCard comic={c} />
    ) : (
      <ComicCard
        comic={c}
        backState={{
          fromPath: `${location.pathname}${location.search}`,
          fromLabel: source?.name ?? 'Source',
        }}
      />
    );

  if (!source) {
    return (
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">Unknown source</h1>
        <p className="text-sm text-muted-foreground">
          No registered source with id <code>{sourceId}</code>.
        </p>
        <Link to="/" className="text-sm underline">
          ← Back to sources
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{source.name}</h1>
        </div>
        {source.capabilities.login && (
          <Link to={`/connect/${source.id}`}>
            <Button variant="outline" size="sm">
              Connect account
            </Button>
          </Link>
        )}
      </header>

      {source.capabilities.search && (
        <div className="flex flex-col gap-1.5">
          <SearchBar
            value={query.q ?? ''}
            onSubmit={handleSearchSubmit}
            onDraftChange={setSearchDraft}
            placeholder={isJmSource ? `Search ${source.name}, album ID, or album URL…` : `Search ${source.name}…`}
            {...(schema || schemaLoading ? { onToggleFilters: () => setFiltersOpen((o) => !o) } : {})}
            activeFiltersCount={activeFiltersCount}
            onOpenSaved={() => setSavedOpen(true)}
            {...(hasAnyQuery ? { onSave: () => setSaveOpen(true) } : {})}
          />
          {isJmSource && (
            <p className="text-xs text-muted-foreground">
              Paste a JM album ID or 18comic album URL to open it directly.
            </p>
          )}
        </div>
      )}

      {hasAnyQuery && <ActiveSearchSummary query={query} onClear={clearFilters} />}

      {filtersOpen && schemaLoading && (
        <div className="rounded-md border border-border/60 bg-card/30 p-4 text-sm text-muted-foreground">
          Loading filters…
        </div>
      )}
      {filtersOpen && schemaError && !schemaLoading && !schema && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          Could not load filters: {schemaError}
        </div>
      )}
      {filtersOpen && schema && (
        <FilterPanel
          schema={schema}
          value={query}
          onApply={(next) => {
            setQuery(withDraftKeyword(next));
            setFiltersOpen(false);
          }}
          onClear={() => {
            clearFilters();
            setFiltersOpen(false);
          }}
          onClose={() => setFiltersOpen(false)}
        />
      )}

      {authReady ? (
        <PaginatedGrid<ComicCardData>
          fetcher={fetchPage}
          resetKey={`${source.id}:${auth ? 'auth' : 'anon'}:${queryKey}`}
          cacheKey={`source:${location.pathname}${location.search}:${auth ? 'auth' : 'anon'}`}
          ariaLabel={`${source.name} gallery list`}
          itemKey={(c) => `${c.source}-${c.id}`}
          renderItem={renderItem}
          emptyState={hasAnyQuery ? <EmptySearchState /> : undefined}
          errorBanner={(err, retry) => (
            <SourceErrorBanner error={err} sourceId={source.id} onRetry={retry} />
          )}
        />
      ) : (
        // Stay on a skeleton while we decrypt the saved cookies.
        // Critical for exhentai: a request issued before this resolves
        // would fire anonymously and hit the worker's 502 path.
        <div
          aria-label={`${source.name} gallery list loading`}
          className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[3/4] w-full" />
          ))}
        </div>
      )}

      <SavedSearchesMenu scope={source.id} open={savedOpen} onOpenChange={setSavedOpen} />
      <SaveSearchDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        suggestedName={query.q?.trim() || 'Saved search'}
        onSave={async (name) => {
          const url = `${location.pathname}${location.search}`;
          await createSavedSearch({ name, source: source.id, url });
          requestAccountDataAutoSync();
        }}
      />
    </div>
  );
}

function EmptySearchState() {
  return (
    <div className="rounded-md border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
      No results for that search.
    </div>
  );
}

function ActiveSearchSummary({ query, onClear }: { query: PageSearchQuery; onClear: () => void }) {
  const filters = [
    ...(query.uploader ? [`uploader:${query.uploader}`] : []),
    ...(query.includeTags ?? []).map((tag) => `include:${tag}`),
    ...(query.excludeTags ?? []).map((tag) => `exclude:${tag}`),
    ...(query.language ? [`language:${query.language}`] : []),
    ...(query.sort ? [`sort:${query.sort}`] : []),
    ...extraFilterLabels(query.extra),
  ];

  if (filters.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      <span>Active filters:</span>
      {filters.map((filter) => (
        <span
          key={filter}
          className="rounded-full border border-border/60 bg-accent/30 px-2 py-0.5 text-foreground"
        >
          {filter}
        </span>
      ))}
      <button
        type="button"
        onClick={onClear}
        className="rounded px-1.5 py-0.5 underline-offset-4 hover:underline"
      >
        Clear
      </button>
    </div>
  );
}

function extraFilterLabels(extra: PageSearchQuery['extra']): string[] {
  if (!extra) return [];
  const labels: string[] = [];
  for (const [key, value] of Object.entries(extra)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      labels.push(`${key}:${value.join(', ')}`);
      continue;
    }
    if (value === true) {
      labels.push(key);
      continue;
    }
    labels.push(`${key}:${String(value)}`);
  }
  return labels;
}
